import type { RigidPose } from './localizerPose';
import { dist, lerp3, poseMultiply, poseGravityAlignedAbout, quatAngle, quatSlerp, quatTilt, sceneFromObservation } from './localizerPose';

/** Monotonic seconds, the clock the stabilizer is fed with. */
export function nowSeconds(): number {
    return performance.now() / 1000;
}

/** One source, one final placement. Retains the existing 40-sample stability,
 * outlier gate/re-lock, time-based smoothing and conditional gravity snap.
 * No mount is returned before acceptance. Reset discards the estimate entirely.
 */
export class LocalizerStabilizer {
    private estimate: RigidPose | null = null;
    private lastTime = -Infinity;
    private samples = 0;
    private outliers = 0;
    private snapped: boolean | null = null;
    private accepted: RigidPose | null = null;
    constructor(private readonly placement: RigidPose) {}
    reset(): void {
        this.estimate = this.accepted = null;
        this.lastTime = -Infinity;
        this.samples = this.outliers = 0;
        this.snapped = null;
    }
    observe(worldFromMarker: RigidPose, timeS: number): RigidPose | null {
        if (this.accepted) return this.accepted;
        if (!Number.isFinite(timeS) || timeS <= this.lastTime
            || ![...worldFromMarker.t, ...worldFromMarker.q].every(Number.isFinite)
            || Math.abs(Math.hypot(...worldFromMarker.q) - 1) > 0.001) return null;
        const raw = sceneFromObservation(worldFromMarker, this.placement);
        if (timeS - this.lastTime > 0.5) this.samples = 0;
        if (this.estimate && (dist(raw.t, this.estimate.t) > 0.25
            || quatAngle(raw.q, this.estimate.q) > 15 * Math.PI / 180)) {
            if (++this.outliers < 8) return null;
            this.estimate = null;
            this.samples = 0;
            this.snapped = null;
        }
        this.outliers = 0;
        const alpha = 1 - Math.exp(-Math.min(timeS - this.lastTime, 1) / 0.15);
        this.estimate = this.estimate ? {
            t: lerp3(this.estimate.t, raw.t, alpha),
            q: quatSlerp(this.estimate.q, raw.q, alpha),
        } : raw;
        this.lastTime = timeS;
        this.samples++;
        const tilt = quatTilt(this.estimate.q);
        const limit = Math.acos(0.85), band = 3 * Math.PI / 180;
        if (this.snapped === null) this.snapped = tilt <= limit;
        else if (this.snapped && tilt > limit + band) this.snapped = false;
        else if (!this.snapped && tilt < limit - band) this.snapped = true;
        const marker = poseMultiply(this.estimate, this.placement);
        const dot = Math.abs(marker.q.reduce((sum, v, i) => sum + v * worldFromMarker.q[i], 0));
        if (this.samples >= 40 && dist(marker.t, worldFromMarker.t) < Math.sqrt(0.001) && dot > 0.999) {
            this.accepted = this.snapped ? poseGravityAlignedAbout(this.estimate, this.placement.t) : this.estimate;
        }
        return this.accepted;
    }
}
